import React from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Form433AReview.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { ShowAlert } from '../../Components/ShowAlert';
import Form433AReviewService from '../../Services/Form433AReviewService';
import ExpenseService from '../../Services/ExpenseService';
import { HelpIcon, HtmlText } from './HtmlText';
import { IAttachment, IForm433AReviewState, ILogicForValue, IQuestion, ISection, ITableColumn, ITableRow } from './interface';
import {
    childColumnKey, clientValueKey, emptyTableRow, forEachQuestion, ROW_NOT_DELETABLE_KEY, systemValueKey, formatDisplayValue, formatPlaceholder, getColumnLabel, getInputMaxLength,
    getVisibleQuestions, isChildVisible, isEmptyTable, IValidationRule, sanitizeInput, toApiTableRows, toTableRows, validateValue,
    evidenceKey, calculateLogics, formatCalculatedValue, getEditableChildColumns, getFileChildColumns, getRowFiles, isConditionMet, newFilesKey, formatFileSize, parseFileSize, isSameNumber, parseNumber
} from './Form433AReviewHelper';
import { createRoot } from 'react-dom/client';
import EvidenceList from './EvidenceList';

/** Proof files the admin can attach; same types as the client app's expense proofs. */
const PROOF_ACCEPT = '.pdf,.png,.jpg,.jpeg';
/** Max total size of the proofs of one expense, including files already attached. */
const PROOF_MAX_TOTAL_MB = 5;

/** Complete Review is hidden until the complete review API is available. */
const SHOW_COMPLETE_REVIEW = true;

type QuestionStatus = 'confirmed' | 'review' | 'missing' | 'empty';

interface IAttentionItem {
    key: string;
    anchorId: string;
    sectionName: string;
    label: string;
    message: string;
    status: 'missing' | 'error';
}

class Form433AReview extends React.Component<{}, IForm433AReviewState> {

    private stickyRef = React.createRef<HTMLDivElement>();
    private scrollContainer: HTMLElement | null = null;
    private scrollFrame = 0;

    componentWillUnmount() {
        this.scrollContainer?.removeEventListener('scroll', this.onContainerScroll);
        cancelAnimationFrame(this.scrollFrame);
    }

    /** Highlights the tab of the section currently under the sticky header. */
    private onContainerScroll = () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
            const stickyBottom = this.stickyRef.current?.getBoundingClientRect().bottom || 0;
            let activeSectionId = this.state.sections.length > 0 ? this.state.sections[0].sectionId : 0;
            this.state.sections.forEach(section => {
                const card = document.getElementById(`f433-section-${section.sectionId}`);
                if (card && card.getBoundingClientRect().top <= stickyBottom + 40) {
                    activeSectionId = section.sectionId;
                }
            });
            if (activeSectionId !== this.state.activeSectionId) {
                this.setState({ activeSectionId });
            }
        });
    };

    constructor(props_: {}) {
        super(props_);

        const urlParams = new URLSearchParams(window.location.search);

        this.state = {
            accessToken: '',
            userId: urlParams.get('userId') || '',
            applicationStatus: urlParams.get('status') || '',
            isReviewed: urlParams.get('isReviewed') === 'true',
            isLoading: true,
            loadError: '',
            sections: [],
            originalAnswers: {},
            answers: {},
            originalTables: {},
            tables: {},
            errors: {},
            newFiles: {},
            activeSectionId: 0
        };
    }

    async componentDidMount() {
        this.scrollContainer = this.stickyRef.current?.closest('.body-container') as HTMLElement | null;
        this.scrollContainer?.addEventListener('scroll', this.onContainerScroll, { passive: true });

        const accesstoken = await loadFromLocalStorage('accessToken');
        if (!accesstoken) {
            localStorage.clear();
            window.location.href = '/';
            return;
        }

        this.setState({ accessToken: accesstoken.toString() }, () => {
            if (this.state.userId) {
                this.getQuestions();
            }
            else {
                this.setState({ isLoading: false, loadError: 'No user selected. Open a user from the Form 433-A Review list.' });
            }
        });
    }

    private getQuestions() {
        Form433AReviewService.getQuestions({ userId: this.state.userId }, this.state.accessToken).then(r => {
            if (r.ok) {
                r.json().then((sections: ISection[]) => this.loadSections(sections || []));
            }
            else {
                this.setState({ isLoading: false, loadError: 'Unable to load the Form 433-A questions for this user.' });
            }
        }).catch(() => {
            this.setState({ isLoading: false, loadError: 'Unable to load the Form 433-A questions for this user.' });
        });
    }

    private loadSections(sections: ISection[]) {
        const sorted = [...sections].sort((a, b) => a.sectionSequence - b.sectionSequence);
        const answers: { [questionId: string]: string } = {};
        const tables: { [questionId: string]: ITableRow[] } = {};

        forEachQuestion(sorted, question => {
            if (question.questionType === 'Table') {
                tables[question.questionId] = toTableRows(question);
            }
            else {
                answers[question.questionId] = question.response || '';
            }
        });

        this.setState({
            isLoading: false,
            loadError: '',
            sections: sorted,
            originalAnswers: answers,
            answers: { ...answers },
            originalTables: tables,
            tables: this.cloneTables(tables),
            errors: {},
            newFiles: {},
            activeSectionId: sorted.length > 0 ? sorted[0].sectionId : 0
        });
    }

    public render() {
        const isReady = !this.state.isLoading && !this.state.loadError;
        return (
            <div className='f433-page'>
                {/* Header, stats and section tabs stay pinned while the form scrolls. */}
                <div className='f433-sticky' ref={this.stickyRef}>
                    {this.renderHeader()}
                    {isReady &&
                        <>
                            {this.isReadOnly() && <div className='f433-readonly-banner'>This Form 433-A has been submitted and is read-only.</div>}
                            {this.renderStats()}
                            {this.renderTabs()}
                        </>
                    }
                </div>
                {
                    this.state.isLoading ? <div className='f433-empty'>Loading Form 433-A...</div> :
                        this.state.loadError ? <div className='f433-empty'>{this.state.loadError}</div> :
                            <>
                                <div className='f433-body'>
                                    <div className='f433-main'>
                                        <div className='f433-flow'>
                                            {['Client Value', 'System Value', 'Evidence', 'AI Review', 'Admin Decision', 'Final Value'].map(x =>
                                                <span key={x}>{x} <ArrowForwardIcon /></span>
                                            )}
                                        </div>
                                        {this.state.sections.map(section => this.renderSection(section))}
                                        {this.renderSummary()}
                                    </div>
                                    {this.renderSidePanel()}
                                </div>
                            </>
                }
            </div>
        );
    }

    private renderHeader() {
        const taxpayerName = this.getTaxpayerName();
        const isReady = !this.state.isLoading && !this.state.loadError;
        return (
            <div className='f433-header'>
                <div>
                    <div className='f433-breadcrumb'><Link to='/form433AReview'>Form 433-A Review</Link>{taxpayerName ? ` / ${taxpayerName}` : ''}</div>
                    <div className='f433-title-row'>
                        <h1>Form 433-A Review</h1>
                        {this.state.applicationStatus && <span className='f433-pill review'><ErrorOutlineIcon /> {this.state.applicationStatus}</span>}
                        <div><span className='f433-subtitle'>Review Status:</span><span className='f433-pill review'><ErrorOutlineIcon />{this.state.isReviewed ? 'Completed' : 'Pending'}</span></div>
                    </div>
                    <div className='f433-subtitle'>{[taxpayerName, 'IRS Form 433-A'].filter(x => x).join(' • ')}</div>
                    {isReady && <div className='f433-meta'>{this.state.sections.length} sections • {this.getCounts().total} questions</div>}
                </div>
                {isReady &&
                    <div className='f433-header-actions'>
                        {!this.isReadOnly() && <button className='f433-btn' onClick={() => this.save(false)}>Save</button>}
                        <button className='f433-btn' onClick={() => this.previewForm()}>Preview 433-A</button>
                        {SHOW_COMPLETE_REVIEW && !this.isReadOnly() && <button className='f433-btn primary' onClick={() => this.completeReview()}>Complete Review</button>}
                    </div>
                }
            </div>
        );
    }

    private renderStats() {
        const counts = this.getCounts();
        const stats = [
            { count: counts.answered, label: 'Answered', cls: 'confirmed' },
            { count: counts.edited, label: 'Edited', cls: 'review' },
            { count: counts.missing, label: 'Missing Required', cls: 'missing' },
            { count: Object.keys(this.state.errors).length, label: 'Validation Errors', cls: 'ai' },
        ];
        return (
            <div className='f433-stats'>
                {stats.map(x =>
                    <div key={x.label} className={`f433-stat ${x.cls}`}>
                        <span className='count'>{x.count}</span> <span>{x.label}</span>
                    </div>
                )}
            </div>
        );
    }

    private renderTabs() {
        return (
            <div className='f433-tabs'>
                {this.state.sections.map(section => {
                    const missing = this.getSectionMissingCount(section);
                    return (
                        <div key={section.sectionId}
                            className={`f433-tab ${this.state.activeSectionId === section.sectionId ? 'active' : ''}`}
                            onClick={() => {
                                this.setState({ activeSectionId: section.sectionId });
                                this.scrollTo(`f433-section-${section.sectionId}`);
                            }}>
                            {this.getShortSectionName(section.sectionName)} {missing > 0 ? <span className='f433-tab-count'>{missing}</span> : '✓'}
                        </div>
                    );
                })}
            </div>
        );
    }

    private renderSection(section: ISection) {
        const missing = this.getSectionMissingCount(section);
        return (
            <div className='f433-card' key={section.sectionId} id={`f433-section-${section.sectionId}`}>
                <div className='f433-card-header'>
                    <h2><HtmlText text={section.sectionName} /></h2>
                    {missing > 0 ? this.renderPill('missing', `${missing} missing`) : this.renderPill('confirmed', 'Complete')}
                </div>
                {section.sectionNote && <div className='f433-section-note'><HtmlText text={section.sectionNote} /></div>}
                {section.fields.map(question => this.renderQuestion(question, 0))}
            </div>
        );
    }

    private renderQuestion(question: IQuestion, depth: number): React.ReactNode {
        const visibleChildren = (question.childQuestion || [])
            .filter(child => isChildVisible(child, this.state.answers[question.questionId]));

        return (
            <React.Fragment key={question.questionId}>
                {question.questionType === 'Table' ? this.renderTableQuestion(question, depth) :
                    question.questionType === 'File' ? this.renderFileQuestion(question, depth) :
                        this.renderFieldQuestion(question, depth)}
                {visibleChildren.map(child => this.renderQuestion(child, depth + 1))}
            </React.Fragment>
        );
    }

    private renderFieldQuestion(question: IQuestion, depth: number) {
        const original = this.state.originalAnswers[question.questionId] || '';
        const current = this.state.answers[question.questionId] || '';
        const error = this.state.errors[question.questionId];
        const status = this.getQuestionStatus(question);
        const canFill = !this.isReadOnly() && !this.isNotEditable(question);

        return (
            <div className={`f433-field depth-${Math.min(depth, 3)}`} key={question.questionId} id={`f433-q-${question.questionId}`}>
                <div className='f433-field-row simple'>
                    <div className='f433-field-label'>
                        <div>
                            {/* {question.sequenceNumber && <span className='f433-seq'>{question.sequenceNumber}</span>} */}
                            <HtmlText text={question.question} />
                            {question.isMandatory ? <span className='f433-required'>*</span> : null}
                            <HelpIcon helpText={question.helpText} />
                        </div>
                        {this.renderPill(status)}
                    </div>
                    {this.renderSourceValue('Client Value', question.clientValue, question.inputFormat,
                        canFill ? value => this.fillAnswer(question, value) : undefined)}
                    <ArrowForwardIcon className='f433-arrow' />
                    {this.renderSourceValue('System Value', question.systemValue, question.inputFormat,
                        canFill ? value => this.fillAnswer(question, value) : undefined,
                        this.isDifferent(question.clientValue, question.systemValue))}
                    {this.renderPendingValue('Evidence', question.evidence)}
                    {this.renderPendingValue('AI Review')}
                    {this.renderPendingValue('Admin Decision')}
                    <ArrowForwardIcon className='f433-arrow' />
                    <div className='f433-value final'>
                        <label>Final Value</label>
                        {this.isNotEditable(question) ?
                            <div className='f433-readonly'>{formatDisplayValue(current, question.inputFormat) || '-'}</div> :
                            question.questionType === 'Checkbox' ?
                                <select className={this.getInputClass(original, current, error)} value={current} disabled={this.isReadOnly()}
                                    onChange={e => this.setAnswer(question.questionId, e.target.value)}>
                                    <option value=''>Select</option>
                                    {question.options.map(x => <option key={x.option} value={x.option}>{x.option}</option>)}
                                </select> :
                                <input type='text' className={this.getInputClass(original, current, error)} value={current}
                                    disabled={this.isReadOnly()}
                                    placeholder={formatPlaceholder(question.inputFormat, question.placeholder)}
                                    inputMode={question.inputType === 'numeric' ? 'decimal' : 'text'}
                                    maxLength={getInputMaxLength(question)}
                                    onChange={e => this.setAnswer(question.questionId, sanitizeInput(e.target.value, question))}
                                    onBlur={e => this.setError(question.questionId, this.getValueError(question, e.target.value, question.fieldName || question.question))} />
                        }
                        {error && <div className='f433-error'>{error}</div>}
                    </div>
                </div>
            </div>
        );
    }

    /** Uploaded proofs (e.g. "Attach Proof") listed with a download link. */
    private renderFileQuestion(question: IQuestion, depth: number) {
        const files = question.files || [];
        const newFiles = this.state.newFiles[question.questionId] || [];
        const total = files.length + newFiles.length;
        const canAttach = !this.isReadOnly() && (!question.maxLength || total < question.maxLength);
        return (
            <div className={`f433-field depth-${Math.min(depth, 3)}`} key={question.questionId} id={`f433-q-${question.questionId}`}>
                <div className='f433-file-row'>
                    <div className='f433-field-label'>
                        <div>
                            {/* {question.sequenceNumber && <span className='f433-seq'>{question.sequenceNumber}</span>} */}
                            <HtmlText text={question.question} />
                            <HelpIcon helpText={question.helpText} />
                        </div>
                        {this.renderPill(newFiles.length > 0 ? 'review' : total > 0 ? 'confirmed' : 'empty', total > 0 ? `${total} file(s)` : 'No files')}
                    </div>
                    <div className='f433-files'>
                        {total === 0 && <span className='f433-help'>No files uploaded.</span>}
                        {files.map(file =>
                            <div className='f433-file' key={file.fileId}>
                                <AttachFileIcon />
                                <button className='f433-link-btn' title='Download' onClick={() => this.downloadFile(file)}>{file.fileName}</button>
                                {file.size && <span className='f433-file-size'>{file.size}</span>}
                                {file.fileStatus && <span className={`f433-pill ${this.getFileStatusClass(file.fileStatus)}`}>{file.fileStatus}</span>}
                                {file.fileComments && <span className='f433-help'>{file.fileComments}</span>}
                            </div>
                        )}
                        {newFiles.map((file, index) =>
                            <div className='f433-file' key={`new-${file.fileName}-${index}`}>
                                <AttachFileIcon />
                                <span className='f433-new-file' title='Uploads when the form is saved'>
                                    {file.fileName}
                                    <CloseIcon className='f433-icon-btn' titleAccess='Remove'
                                        onClick={() => this.removeQuestionFile(question.questionId, index)} />
                                </span>
                                <span className='f433-file-size'>{file.size}</span>
                            </div>
                        )}
                        {canAttach &&
                            <div>
                                <label className='f433-attach-btn'>
                                    + Attach
                                    <input type='file' hidden multiple accept={PROOF_ACCEPT}
                                        onChange={e => {
                                            this.attachQuestionFiles(question, Array.from(e.target.files || []));
                                            e.target.value = '';
                                        }} />
                                </label>
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    private renderTableQuestion(question: IQuestion, depth: number) {
        const columns = question.columns || [];
        const rows = this.state.tables[question.questionId] || [];
        const originalRows = this.state.originalTables[question.questionId] || [];
        const status = this.getQuestionStatus(question);

        return (
            <div className={`f433-field depth-${Math.min(depth, 3)}`} key={question.questionId} id={`f433-q-${question.questionId}`}>
                <div className='f433-table-header'>
                    <div className='f433-field-label'>
                        <div>
                            {/* {question.sequenceNumber && <span className='f433-seq'>{question.sequenceNumber}</span>} */}
                            <HtmlText text={question.question} />
                            <HelpIcon helpText={question.helpText} />
                        </div>
                        {this.renderPill(status, status === 'empty' ? 'No rows' : undefined)}
                    </div>
                    {!this.isReadOnly() && !this.isMaxRowsReached(question, rows) &&
                        <button className='f433-btn small' onClick={() => this.addTableRow(question)}>+ Add row</button>}
                </div>
                {rows.length > 0 &&
                    <div className='f433-table-wrap'>
                        <table className='f433-table'>
                            <thead>
                                <tr>
                                    {columns.map(column =>
                                        <th key={column.columnId}>
                                            <HtmlText text={getColumnLabel(column)} />
                                            {column.isMandatory ? <span className='f433-required'>*</span> : null}
                                            {getColumnLabel(column) !== column.columnName && <HelpIcon helpText={column.columnName} />}
                                        </th>
                                    )}
                                    {!this.isReadOnly() && <th className='f433-table-action'></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rowIndex) =>
                                    <tr key={rowIndex}>
                                        {columns.map(column =>
                                            <td key={column.columnId}>
                                                {this.renderTableCell(question, column, row, originalRows[rowIndex], rowIndex)}
                                            </td>
                                        )}
                                        {!this.isReadOnly() &&
                                            <td className='f433-table-action'>
                                                {!row[ROW_NOT_DELETABLE_KEY] &&
                                                    <DeleteOutlineIcon className='f433-icon-btn' titleAccess='Remove row'
                                                        onClick={() => this.removeTableRow(question.questionId, rowIndex)} />
                                                }
                                            </td>
                                        }
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                }
            </div>
        );
    }

    private renderTableCell(question: IQuestion, column: ITableColumn, row: ITableRow, originalRow: ITableRow | undefined, rowIndex: number) {
        const errorKey = this.getCellKey(question.questionId, rowIndex, column.columnId);
        const error = this.state.errors[errorKey];
        const value = row[column.columnId] || '';
        const original = originalRow ? (originalRow[column.columnId] || '') : '';
        const onChange = (key: string, newValue: string) => this.setTableCell(question.questionId, rowIndex, key, newValue);
        const hasOptions = (column.questionType === 'Dropdown' || column.questionType === 'Checkbox') && column.options.length > 0;
        const childErrors = this.getVisibleChildColumns(column, row)
            .map(({ index }) => this.state.errors[this.getCellKey(question.questionId, rowIndex, childColumnKey(column.columnId, index))])
            .filter(x => x);
        const clientValue = row[clientValueKey(column.columnId)];
        const systemValue = row[systemValueKey(column.columnId)];
        const evidenceValue = row[evidenceKey(column.columnId)];

        const canFill = (!this.isReadOnly() && !this.isCalculated(column)) || evidenceValue;
        const fillCell = (newValue: string, isEvidence: boolean = false) => {
            if (isEvidence) {
                this.openEvidenceList();
            }
            else {
                onChange(column.columnId, newValue);
                this.setError(errorKey, this.getCellError(column, newValue, rowIndex));
            }
        };

        return (
            <>
                {this.isCalculated(column) ?
                    <div className='f433-readonly'>{formatDisplayValue(value, column.inputFormat) || '-'}</div> :
                    hasOptions ?
                        <select className={this.getInputClass(original, value, error)} value={value} disabled={this.isReadOnly()}
                            onChange={e => onChange(column.columnId, e.target.value)}>
                            <option value=''>Select</option>
                            {column.options.map(x => <option key={x.option} value={x.value || x.option}>{x.option}</option>)}
                        </select> :
                        <input type='text' className={this.getInputClass(original, value, error)} value={value}
                            disabled={this.isReadOnly()}
                            placeholder={formatPlaceholder(column.inputFormat, column.placeholder)}
                            inputMode={column.inputType === 'numeric' ? 'decimal' : 'text'}
                            maxLength={getInputMaxLength(column)}
                            onChange={e => onChange(column.columnId, sanitizeInput(e.target.value, column))}
                            onBlur={e => this.setError(errorKey, this.getCellError(column, e.target.value, rowIndex))} />
                }
                {(clientValue !== undefined || systemValue !== undefined) &&
                    <div className='f433-cell-sources'>
                        {this.renderSourceChip('Client', clientValue, column.inputFormat, canFill ? fillCell : undefined)}
                        {this.renderSourceChip('System', systemValue, column.inputFormat, canFill ? fillCell : undefined,
                            this.isDifferent(clientValue, systemValue))}
                        {this.renderSourceChip('Evidence', evidenceValue, column.inputFormat, canFill ? fillCell : undefined, false, true)}
                    </div>
                }
                {this.getVisibleChildColumns(column, row).map(({ child, index }) => {
                    const key = childColumnKey(column.columnId, index);
                    return (
                        <div className='f433-child-column' key={key}>
                            <span>{getColumnLabel(child)}</span>
                            <input type='text' value={row[key] || ''}
                                className={this.state.errors[this.getCellKey(question.questionId, rowIndex, key)] ? 'invalid' : ''}
                                disabled={this.isReadOnly()}
                                placeholder={formatPlaceholder(child.inputFormat, child.placeholder)}
                                maxLength={getInputMaxLength(child)}
                                onChange={e => onChange(key, sanitizeInput(e.target.value, child))}
                                onBlur={e => this.setError(this.getCellKey(question.questionId, rowIndex, key), this.getCellError(child, e.target.value, rowIndex))} />
                        </div>
                    );
                })}
                {this.renderCellProofs(question.questionId, column, row, rowIndex)}
                {error && <div className='f433-error'>{error}</div>}
                {childErrors.map(x => <div className='f433-error' key={x}>{x}</div>)}
            </>
        );
    }

    /** "Attach Proof" child column, shown when the cell value meets its condition (e.g. amount > 0). */
    private renderCellProofs(questionId: string, column: ITableColumn, row: ITableRow, rowIndex: number) {
        const proofColumn = getFileChildColumns(column)
            .find(child => isConditionMet(row[column.columnId], child.matchingValue, child.matchingCondition));
        if (!proofColumn) {
            return null;
        }
        const files = getRowFiles(row, column.columnId);
        const newFiles = getRowFiles(row, column.columnId, true);
        const canAttach = !this.isReadOnly() && (!proofColumn.maxLength || files.length + newFiles.length < proofColumn.maxLength);
        return (
            <div className='f433-cell-proof'>
                <span><AttachFileIcon /> {getColumnLabel(proofColumn)}:</span>
                {files.map(file =>
                    <button key={file.fileId} className='f433-link-btn' title='Download' onClick={() => this.downloadFile(file)}>{file.fileName}</button>
                )}
                {newFiles.map((file, index) =>
                    <span key={`${file.fileName}-${index}`} className='f433-new-file' title='Uploads when the form is saved'>
                        {file.fileName}
                        {!this.isReadOnly() &&
                            <CloseIcon className='f433-icon-btn' titleAccess='Remove'
                                onClick={() => this.removeProofFile(questionId, rowIndex, column.columnId, index)} />}
                    </span>
                )}
                {files.length + newFiles.length === 0 &&
                    <span className='f433-cell-proof-missing'>{proofColumn.validationMessage || 'No proof attached.'}</span>}
                {canAttach &&
                    <label className='f433-attach-btn'>
                        + Attach
                        <input type='file' hidden multiple accept={PROOF_ACCEPT}
                            onChange={e => {
                                this.attachProofFiles(questionId, rowIndex, column.columnId, proofColumn, Array.from(e.target.files || []));
                                e.target.value = '';
                            }} />
                    </label>
                }
            </div>
        );
    }

    /** Client or system value; clicking it copies the value into the final value. */
    private renderSourceValue(label: string, value: string | undefined, inputFormat: string | null,
        onFill?: (value: string) => void, highlight?: boolean) {
        const hasValue = this.hasValue(value);
        const clickable = hasValue && !!onFill;
        return (
            <div className='f433-value'>
                <label>{label}</label>
                <div className={`f433-source ${clickable ? 'clickable' : ''} ${highlight ? 'highlight' : ''}`}
                    title={clickable ? 'Click to use as final value' : undefined}
                    role={clickable ? 'button' : undefined}
                    onClick={clickable ? () => onFill!(String(value).trim()) : undefined}>
                    {hasValue ? formatDisplayValue(String(value), inputFormat) : '-'}
                </div>
            </div>
        );
    }

    /** Placeholder column until the API provides this data. */
    private renderPendingValue(label: string, value?: string) {
        return (
            <div className='f433-value'>
                <label>{label}</label>
                <div className='f433-source'>{value || '-'}</div>
            </div>
        );
    }

    private renderSourceChip(label: string, value: string | undefined, inputFormat: string | null,
        onFill?: (value: string, isEvidence: boolean) => void, highlight?: boolean, isEvidence: boolean = false) {
        const hasValue = this.hasValue(value);
        const clickable = hasValue && !!onFill;
        return (
            <span className={`f433-source-chip ${clickable ? 'clickable' : ''} ${highlight ? 'highlight' : ''}`}
                title={clickable ? 'Click to use as final value' : undefined}
                onClick={clickable ? () => onFill!(String(value).trim(), isEvidence) : undefined}>
                {label}: {hasValue ? formatDisplayValue(String(value), inputFormat) : '-'}
            </span>
        );
    }

    private renderPill(status: QuestionStatus, text?: string) {
        const defaults = { confirmed: 'Answered', review: 'Edited', missing: 'Missing', empty: 'Not answered' };
        const icon = status === 'confirmed' ? <CheckCircleOutlineIcon /> : status === 'review' ? <EditOutlinedIcon /> :
            status === 'missing' ? <HighlightOffIcon /> : <ErrorOutlineIcon />;
        return <span className={`f433-pill ${status}`}>{icon} {text || defaults[status]}</span>;
    }

    private renderSummary() {
        const counts = this.getCounts();
        const errorCount = Object.keys(this.state.errors).length;
        const items = [
            { count: this.state.sections.length, label: 'sections' },
            { count: counts.answered, label: 'questions answered' },
            { count: counts.edited, label: 'fields edited by admin' },
            { count: counts.missing, label: 'required fields missing' },
            { count: counts.tableRows, label: 'table rows' },
        ];
        const message = counts.missing > 0 || errorCount > 0 ?
            'Resolve missing required fields and validation errors before completing the review.' :
            'All required fields are answered. Ready to complete the review.';

        return (
            <div className='f433-summary'>
                <h2>Review Summary</h2>
                <div className='f433-summary-grid'>
                    {items.map(x =>
                        <div key={x.label}>
                            <div className='count'>{x.count}</div>
                            <div className='label'>{x.label}</div>
                        </div>
                    )}
                </div>
                <div className='f433-summary-footer'>
                    <span>{this.isReadOnly() ? 'This Form 433-A has been submitted and is read-only.' : message}</span>
                    {SHOW_COMPLETE_REVIEW && !this.isReadOnly() && <button className='f433-btn primary' onClick={() => this.completeReview()}>Complete 433-A Review</button>}
                </div>
            </div>
        );
    }

    private renderSidePanel() {
        const items = this.getAttentionItems();
        return (
            <div className='f433-side'>
                <div className='f433-side-tabs'>
                    <div className='active'>AI Review ({items.length})</div>
                </div>
                <div className='f433-side-body'>
                    <h3>Automated Form 433-A Review</h3>
                    <div className='f433-side-sub'>
                        {items.length > 0 ? `${items.length} item${items.length === 1 ? '' : 's'} need${items.length === 1 ? 's' : ''} attention` : 'Nothing needs attention.'}
                    </div>
                    {items.map(item =>
                        <div key={item.key} className={`f433-flag ${item.status === 'error' ? 'high' : 'medium'}`}>
                            <div className='f433-flag-sev'><strong>{item.status === 'error' ? 'ERROR' : 'MISSING'}</strong> {item.sectionName}</div>
                            <div className='f433-flag-title'><HtmlText text={item.label} /></div>
                            <div className='f433-flag-detail'>{item.message}</div>
                            <div className='f433-flag-actions'>
                                <button className='f433-link-btn' onClick={() => this.scrollTo(item.anchorId)}>Review Field</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // #region Data helpers

    private getQuestionStatus(question: IQuestion): QuestionStatus {
        if (question.questionType === 'File') {
            if ((this.state.newFiles[question.questionId] || []).length > 0) {
                return 'review';
            }
            return (question.files || []).length > 0 ? 'confirmed' : 'empty';
        }
        if (question.questionType === 'Table') {
            const rows = this.state.tables[question.questionId];
            if (JSON.stringify(rows) !== JSON.stringify(this.state.originalTables[question.questionId])) {
                return 'review';
            }
            if (isEmptyTable(rows)) {
                return question.isMandatory ? 'missing' : 'empty';
            }
            return 'confirmed';
        }

        const current = (this.state.answers[question.questionId] || '').trim();
        if (current !== (this.state.originalAnswers[question.questionId] || '').trim()) {
            return 'review';
        }
        if (!current) {
            return question.isMandatory && !this.isNotEditable(question) ? 'missing' : 'empty';
        }
        return 'confirmed';
    }

    private hasValue(value: string | undefined | null) {
        return value !== undefined && value !== null && String(value).trim() !== '';
    }

    /** True when both values exist and differ, so the system value is highlighted for review. */
    private isDifferent(clientValue?: string, systemValue?: string) {
        return this.hasValue(clientValue) && this.hasValue(systemValue) &&
            String(clientValue).trim().toLowerCase() !== String(systemValue).trim().toLowerCase();
    }

    /** Editable child columns (e.g. "Lender") whose condition is met by the cell value. */
    private getVisibleChildColumns(column: ITableColumn, row: ITableRow) {
        return getEditableChildColumns(column)
            .filter(({ child }) => isConditionMet(row[column.columnId], child.matchingValue, child.matchingCondition));
    }

    /** Tables with a maxLength (e.g. 1 for single-row expenses) cannot get more rows. */
    private isMaxRowsReached(question: IQuestion, rows: ITableRow[]) {
        return !!question.maxLength && rows.length >= question.maxLength;
    }

    /** Submitted forms can be viewed but not edited. */
    private isReadOnly() {
        return this.state.applicationStatus.trim().toLowerCase() === 'submitted';
    }

    /**
     * The admin can edit every answer, including ones the API marks read-only for the client.
     * Only calculated totals (read-only with a formula) and file uploads stay non-editable.
     */
    private isNotEditable(question: IQuestion) {
        return question.questionType === 'File' || this.isCalculated(question);
    }

    private isCalculated(field: IQuestion | ITableColumn) {
        return !!field.isReadonlyField && (field.logicsForValue || []).length > 0;
    }

    private getCounts() {
        let total = 0, answered = 0, edited = 0, missing = 0, tableRows = 0;
        this.state.sections.forEach(section => {
            getVisibleQuestions(section, this.state.answers).forEach(question => {
                total++;
                const status = this.getQuestionStatus(question);
                if (status === 'confirmed') answered++;
                if (status === 'review') { answered++; edited++; }
                if (status === 'missing') missing++;
                if (question.questionType === 'Table') {
                    tableRows += (this.state.tables[question.questionId] || []).length;
                }
            });
        });
        return { total, answered, edited, missing, tableRows };
    }

    private getSectionMissingCount(section: ISection) {
        return getVisibleQuestions(section, this.state.answers)
            .filter(question => this.getQuestionStatus(question) === 'missing').length;
    }

    private getAttentionItems(): IAttentionItem[] {
        const items: IAttentionItem[] = [];
        this.state.sections.forEach(section => {
            const sectionName = this.getShortSectionName(section.sectionName);
            getVisibleQuestions(section, this.state.answers).forEach(question => {
                const anchorId = `f433-q-${question.questionId}`;
                const label = question.fieldName || question.question;
                const fieldError = this.state.errors[question.questionId];
                if (fieldError) {
                    items.push({ key: question.questionId, anchorId, sectionName, label, message: fieldError, status: 'error' });
                }
                else if (this.getQuestionStatus(question) === 'missing') {
                    items.push({ key: question.questionId, anchorId, sectionName, label, message: 'Required field has no value.', status: 'missing' });
                }
                if (question.questionType === 'Table') {
                    const tableErrors = Object.keys(this.state.errors).filter(key => key.startsWith(`${question.questionId}|`));
                    tableErrors.forEach(key => items.push({ key, anchorId, sectionName, label, message: this.state.errors[key], status: 'error' }));
                }
            });
        });
        return items;
    }

    private getTaxpayerName() {
        if (this.state.sections.length === 0) {
            return '';
        }
        const personal = this.state.sections[0].fields;
        const firstName = personal.find(x => x.fieldName === 'First Name');
        const lastName = personal.find(x => x.fieldName === 'Last Name');
        return [firstName, lastName].map(x => x ? this.state.answers[x.questionId] : '').filter(x => x).join(' ');
    }

    private getShortSectionName(sectionName: string) {
        return sectionName.replace(/\s*\(.*\)\s*/g, '').trim();
    }

    private getInputClass(original: string, current: string, error?: string) {
        if (error) {
            return 'invalid';
        }
        return (original || '').trim() !== (current || '').trim() ? 'highlight' : '';
    }

    private getCellKey(questionId: string, rowIndex: number, columnId: string) {
        return `${questionId}|${rowIndex}|${columnId}`;
    }

    private cloneTables(tables: { [questionId: string]: ITableRow[] }) {
        const clone: { [questionId: string]: ITableRow[] } = {};
        Object.keys(tables).forEach(key => {
            clone[key] = tables[key].map(row => ({ ...row }));
        });
        return clone;
    }

    // #endregion

    // #region Edit actions

    private setAnswer(questionId: string, value: string) {
        const errors = { ...this.state.errors };
        delete errors[questionId];
        this.setState({ ...this.applyCalculations({ ...this.state.answers, [questionId]: value }, this.state.tables), errors });
    }

    /** Copies a client/system value into the final value and validates it. */
    private fillAnswer(question: IQuestion, value: string) {
        const error = this.getValueError(question, value, question.fieldName || question.question);
        this.setState(prev => {
            const errors = { ...prev.errors };
            if (error) {
                errors[question.questionId] = error;
            }
            else {
                delete errors[question.questionId];
            }
            return { ...this.applyCalculations({ ...prev.answers, [question.questionId]: value }, prev.tables), errors };
        });
    }

    private setError(key: string, error: string) {
        const errors = { ...this.state.errors };
        if (error) {
            errors[key] = error;
        }
        else {
            delete errors[key];
        }
        this.setState({ errors });
    }

    private setTableCell(questionId: string, rowIndex: number, key: string, value: string) {
        const rows = (this.state.tables[questionId] || []).map((row, index) => index === rowIndex ? { ...row, [key]: value } : row);
        const errors = { ...this.state.errors };
        delete errors[this.getCellKey(questionId, rowIndex, key)];
        this.setState({ ...this.applyCalculations(this.state.answers, { ...this.state.tables, [questionId]: rows }), errors });
    }

    private addTableRow(question: IQuestion) {
        const rows = [...(this.state.tables[question.questionId] || []), emptyTableRow(question.columns || [])];
        this.setState(this.applyCalculations(this.state.answers, { ...this.state.tables, [question.questionId]: rows }));
    }

    private removeTableRow(questionId: string, rowIndex: number) {
        const rows = (this.state.tables[questionId] || []).filter((_row, index) => index !== rowIndex);
        // Row indexes shift after removal, so clear this table's cell errors.
        const errors = { ...this.state.errors };
        Object.keys(errors).filter(key => key.startsWith(`${questionId}|`)).forEach(key => delete errors[key]);
        this.setState({ ...this.applyCalculations(this.state.answers, { ...this.state.tables, [questionId]: rows }), errors });
    }

    /** Reads the selected proofs as base64; they are uploaded with the table on save. */
    private attachProofFiles(questionId: string, rowIndex: number, columnId: string, proofColumn: ITableColumn, selected: File[]) {
        const row = (this.state.tables[questionId] || [])[rowIndex] || {};
        const attached = [...getRowFiles(row, columnId), ...getRowFiles(row, columnId, true)];
        this.readProofFiles(selected, attached, proofColumn.maxLength).then(attached => {
            if (attached.length > 0) {
                const currentRow = (this.state.tables[questionId] || [])[rowIndex] || {};
                const newFiles = [...getRowFiles(currentRow, columnId, true), ...attached];
                this.setTableCell(questionId, rowIndex, newFilesKey(columnId), JSON.stringify(newFiles));
            }
        });
    }

    /** Proofs for a File question (e.g. "Attach Proof" under an expense); uploaded on save. */
    private attachQuestionFiles(question: IQuestion, selected: File[]) {
        const attached = [...(question.files || []), ...(this.state.newFiles[question.questionId] || [])];
        this.readProofFiles(selected, attached, question.maxLength).then(attached => {
            if (attached.length > 0) {
                this.setState(prev => ({
                    newFiles: { ...prev.newFiles, [question.questionId]: [...(prev.newFiles[question.questionId] || []), ...attached] }
                }));
            }
        });
    }

    private removeQuestionFile(questionId: string, fileIndex: number) {
        this.setState(prev => ({
            newFiles: { ...prev.newFiles, [questionId]: (prev.newFiles[questionId] || []).filter((_file, index) => index !== fileIndex) }
        }));
    }

    /**
     * Validates the selected proofs (type, max count from the API, total size of all proofs
     * up to PROOF_MAX_TOTAL_MB) and reads them as base64.
     */
    private readProofFiles(selected: File[], attached: IAttachment[], maxFiles: number | null): Promise<IAttachment[]> {
        const allowed = PROOF_ACCEPT.split(',');
        const invalidType = selected.filter(file => !allowed.some(ext => file.name.toLowerCase().endsWith(ext)));
        let valid = selected.filter(file => !invalidType.includes(file));
        const tooMany = !!maxFiles && valid.length > maxFiles - attached.length;
        if (maxFiles) {
            valid = valid.slice(0, Math.max(maxFiles - attached.length, 0));
        }
        // Files are added in the selected order while the total stays within the limit.
        const maxBytes = PROOF_MAX_TOTAL_MB * 1024 * 1024;
        let totalBytes = attached.reduce((total, file) => total + parseFileSize(file.size), 0);
        const withinSize = valid.filter(file => {
            if (totalBytes + file.size > maxBytes) {
                return false;
            }
            totalBytes += file.size;
            return true;
        });
        const tooLarge = withinSize.length < valid.length;
        valid = withinSize;

        if (invalidType.length > 0) {
            ShowAlert('', 'Only PDF, PNG or JPG files can be attached.', 'error');
        }
        else if (tooLarge) {
            ShowAlert('', `The total size of the attached proofs cannot exceed ${PROOF_MAX_TOTAL_MB} MB.`, 'error');
        }
        else if (tooMany) {
            ShowAlert('', `A maximum of ${maxFiles} proofs can be attached.`, 'error');
        }
        if (valid.length === 0) {
            return Promise.resolve([]);
        }

        return Promise.all(valid.map(file => this.readAsBase64(file).then(base64 => ({
            fileId: '',
            fileName: file.name,
            size: formatFileSize(file.size),
            fileStatus: '',
            fileComments: null,
            fileBase64String: base64
        } as IAttachment)))).catch(() => {
            ShowAlert('', 'Unable to read the selected file.', 'error');
            return [];
        });
    }

    private removeProofFile(questionId: string, rowIndex: number, columnId: string, fileIndex: number) {
        const row = (this.state.tables[questionId] || [])[rowIndex] || {};
        const newFiles = getRowFiles(row, columnId, true).filter((_file, index) => index !== fileIndex);
        this.setTableCell(questionId, rowIndex, newFilesKey(columnId), newFiles.length > 0 ? JSON.stringify(newFiles) : '');
    }

    private readAsBase64(file: File) {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    /**
     * Recalculates totals (logicsForValue) after an edit, the same way the backend does:
     * a step reads a text answer, a cell of the same row, the first row, or the SUM of a column.
     * Hidden questions count as 0. Totals can depend on other totals, so it repeats until stable.
     */
    private applyCalculations(answers: { [questionId: string]: string }, tables: { [questionId: string]: ITableRow[] }) {
        const nextAnswers = { ...answers };
        const nextTables = { ...tables };
        const calculatedQuestions: IQuestion[] = [];
        const calculatedTables: IQuestion[] = [];
        forEachQuestion(this.state.sections, question => {
            if (question.questionType === 'Table') {
                if ((question.columns || []).some(column => this.isCalculated(column))) {
                    calculatedTables.push(question);
                }
            }
            else if (this.isCalculated(question)) {
                calculatedQuestions.push(question);
            }
        });

        for (let pass = 0; pass < 5; pass++) {
            let changed = false;
            const visibleIds = new Set<string>();
            this.state.sections.forEach(section => getVisibleQuestions(section, nextAnswers).forEach(x => visibleIds.add(x.questionId)));

            const getStepValue = (step: ILogicForValue, tableId?: string, row?: ITableRow) => {
                const questionId = step.actionQuestionId || '';
                const columnId = step.actionColumnId;
                if (!visibleIds.has(questionId)) {
                    return 0;
                }
                if (!columnId) {
                    return parseNumber(nextAnswers[questionId]);
                }
                if (row && questionId === tableId && step.sameColumnAction !== 'SUM') {
                    return parseNumber(row[columnId]);
                }
                const rows = nextTables[questionId] || [];
                if (step.sameColumnAction === 'SUM') {
                    return rows.reduce((total, x) => total + parseNumber(x[columnId]), 0);
                }
                return rows.length > 0 ? parseNumber(rows[0][columnId]) : 0;
            };

            calculatedTables.forEach(question => {
                const columns = (question.columns || []).filter(column => this.isCalculated(column));
                let tableChanged = false;
                const rows = (nextTables[question.questionId] || []).map(row => {
                    let newRow = row;
                    columns.forEach(column => {
                        const value = calculateLogics(column.logicsForValue || [], step => getStepValue(step, question.questionId, newRow));
                        if (!isSameNumber(newRow[column.columnId], value)) {
                            newRow = { ...newRow, [column.columnId]: formatCalculatedValue(value, column.inputFormat) };
                            tableChanged = true;
                        }
                    });
                    return newRow;
                });
                if (tableChanged) {
                    nextTables[question.questionId] = rows;
                    changed = true;
                }
            });

            calculatedQuestions.forEach(question => {
                const value = calculateLogics(question.logicsForValue || [], step => getStepValue(step));
                if (!isSameNumber(nextAnswers[question.questionId], value)) {
                    nextAnswers[question.questionId] = formatCalculatedValue(value, question.inputFormat);
                    changed = true;
                }
            });

            if (!changed) {
                break;
            }
        }

        return { answers: nextAnswers, tables: nextTables };
    }

    // #endregion

    // #region Validation and save

    /**
     * Validates all visible questions. Format rules are always checked;
     * required checks only when completing the review.
     */
    private validate(checkRequired: boolean) {
        const errors: { [key: string]: string } = {};

        this.state.sections.forEach(section => {
            getVisibleQuestions(section, this.state.answers).forEach(question => {
                if (this.isNotEditable(question)) {
                    return;
                }
                const label = question.fieldName || question.question;

                if (question.questionType === 'Table') {
                    const rows = this.state.tables[question.questionId] || [];
                    if (checkRequired && question.isMandatory && isEmptyTable(rows)) {
                        errors[question.questionId] = `${label} requires at least one row.`;
                    }
                    rows.forEach((row, rowIndex) => {
                        (question.columns || []).filter(column => !this.isCalculated(column)).forEach(column => {
                            const cellError = this.getCellError(column, row[column.columnId], rowIndex, checkRequired);
                            if (cellError) {
                                errors[this.getCellKey(question.questionId, rowIndex, column.columnId)] = cellError;
                            }
                            this.getVisibleChildColumns(column, row).forEach(({ child, index }) => {
                                const key = childColumnKey(column.columnId, index);
                                const childError = this.getCellError(child, row[key], rowIndex);
                                if (childError) {
                                    errors[this.getCellKey(question.questionId, rowIndex, key)] = childError;
                                }
                            });
                        });
                    });
                    return;
                }

                const error = this.getValueError(question, this.state.answers[question.questionId], label, checkRequired);
                if (error) {
                    errors[question.questionId] = error;
                }
            });
        });

        this.setState({ errors });
        return errors;
    }

    /** Required is only enforced when completing the review; format rules always apply. */
    private getValueError(rule: IQuestion | ITableColumn, value: string, label: string, checkRequired: boolean = false) {
        return validateValue(value, { ...rule, isMandatory: checkRequired && !!rule.isMandatory } as IValidationRule, label);
    }

    private getCellError(column: ITableColumn, value: string, rowIndex: number, checkRequired: boolean = false) {
        const error = this.getValueError(column, value, getColumnLabel(column), checkRequired);
        return error ? `Row ${rowIndex + 1}: ${error}` : '';
    }

    /**
     * Visible questions the admin modified, in the saveAsDraft format.
     * Calculated totals and file uploads are never sent.
     */
    private buildSavePayload() {
        const fields: any[] = [];

        this.state.sections.forEach(section => {
            getVisibleQuestions(section, this.state.answers).forEach(question => {
                if (question.questionType === 'Table') {
                    const rows = this.state.tables[question.questionId] || [];
                    if (JSON.stringify(rows) === JSON.stringify(this.state.originalTables[question.questionId] || [])) {
                        return;
                    }
                    fields.push({
                        questionId: question.questionId,
                        questionType: question.questionType,
                        updatedColumn: toApiTableRows(question.columns || [], this.state.tables[question.questionId] || [])
                    });
                }
                else if (question.questionType === 'File') {
                    const newFiles = this.state.newFiles[question.questionId] || [];
                    if (newFiles.length === 0) {
                        return;
                    }
                    // The API replaces a File question's files, so the saved ones are sent again with their ids.
                    fields.push({
                        questionId: question.questionId,
                        questionType: question.questionType,
                        files: [
                            ...(question.files || []).map(file => ({
                                fileId: file.fileId, fileName: file.fileName, size: file.size,
                                fileStatus: file.fileStatus, fileComments: file.fileComments
                            })),
                            ...newFiles.map(file => ({ fileName: file.fileName, size: file.size, fileBase64String: file.fileBase64String }))
                        ]
                    });
                }
                else if (!this.isNotEditable(question)) {
                    const current = (this.state.answers[question.questionId] || '').trim();
                    if (current === (this.state.originalAnswers[question.questionId] || '').trim()) {
                        return;
                    }
                    fields.push({
                        questionId: question.questionId,
                        questionType: question.questionType,
                        response: (this.state.answers[question.questionId] || '').trim()
                    });
                }
            });
        });

        return { userId: this.state.userId, fields };
    }

    private hasUnsavedChanges() {
        return JSON.stringify(this.state.answers) !== JSON.stringify(this.state.originalAnswers) ||
            JSON.stringify(this.state.tables) !== JSON.stringify(this.state.originalTables) ||
            Object.keys(this.state.newFiles).some(key => this.state.newFiles[key].length > 0);
    }

    private save(isSubmit: boolean, options?: { silent?: boolean }) {
        const errors = this.validate(isSubmit);
        const errorKeys = Object.keys(errors);
        if (errorKeys.length > 0) {
            ShowAlert('', `Please fix ${errorKeys.length} field(s) before ${isSubmit ? 'completing the review' : 'saving'}.`, 'error');
            this.scrollToFirstError(errorKeys[0]);
            return Promise.resolve(false);
        }

        const payload = this.buildSavePayload();
        if (!isSubmit && payload.fields.length === 0) {
            if (!options?.silent) {
                ShowAlert('', 'There are no changes to save.', 'info');
            }
            return Promise.resolve(true);
        }
        // Newly attached proofs get their file ids from the API, so the questions are reloaded after saving.
        const hasNewFiles = JSON.stringify(payload).includes('fileBase64String');
        const request = isSubmit ?
            Form433AReviewService.completeReview(payload, this.state.accessToken) :
            Form433AReviewService.saveAsDraft(payload, this.state.accessToken);

        return request.then(r => {
            if (r.ok) {
                this.setState({
                    originalAnswers: { ...this.state.answers },
                    originalTables: this.cloneTables(this.state.tables),
                    newFiles: {}
                });
                if (hasNewFiles) {
                    this.getQuestions();
                }
                if (!options?.silent) {
                    ShowAlert('', isSubmit ? 'Form 433-A review completed successfully.' : 'Form 433-A saved successfully.', 'success');
                }
                return true;
            }
            return false;
        }).catch(() => false);
    }

    /** The preview is generated from saved data, so unsaved changes are saved as a draft first (with confirmation). */
    private previewForm() {
        if (this.isReadOnly() || !this.hasUnsavedChanges()) {
            this.loadPreview();
            return;
        }

        Swal.fire({
            title: 'Unsaved changes',
            text: 'The preview is generated from saved values. Save your changes as a draft before previewing?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Save & Preview',
            denyButtonText: 'Preview without saving'
        }).then(result => {
            if (result.isConfirmed) {
                this.save(false, { silent: true }).then(isSaved => {
                    if (isSaved) {
                        this.loadPreview();
                    }
                });
            }
            else if (result.isDenied) {
                this.loadPreview();
            }
        });
    }

    private loadPreview() {
        Form433AReviewService.preview({ userId: this.state.userId }, this.state.accessToken).then(r => {
            if (r.ok) {
                r.json().then(resp => {
                    if (!resp?.body) {
                        ShowAlert('', 'Unable to generate the Form 433-A preview.', 'error');
                        return;
                    }
                    this.showPdf(resp.isBase64Encoded === false ? btoa(resp.body) : resp.body);
                });
            }
        });
    }

    private downloadFile(file: IAttachment) {
        ExpenseService.downloadExpenseProof({ fileId: file.fileId, fileName: file.fileName }, this.state.accessToken).then(r => {
            if (r.ok) {
                r.json().then(resp => {
                    if (!resp?.body) {
                        ShowAlert('', 'Unable to download the file.', 'error');
                        return;
                    }
                    const byteCharacters = atob(String(resp.body).replace(/\s/g, ''));
                    const byteArray = new Uint8Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteArray[i] = byteCharacters.charCodeAt(i);
                    }
                    const url = URL.createObjectURL(new Blob([byteArray], { type: resp.headers?.['Content-Type'] || this.getMimeType(file.fileName) }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                });
            }
        });
    }

    private getMimeType(fileName: string) {
        const extension = (fileName.split('.').pop() || '').toLowerCase();
        switch (extension) {
            case 'pdf': return 'application/pdf';
            case 'png': return 'image/png';
            case 'jpg':
            case 'jpeg': return 'image/jpeg';
            case 'gif': return 'image/gif';
            default: return 'application/octet-stream';
        }
    }

    private getFileStatusClass(fileStatus: string): QuestionStatus {
        const status = fileStatus.toLowerCase();
        if (status === 'approved') return 'confirmed';
        if (status === 'rejected') return 'missing';
        return 'review';
    }

    private showPdf(base64: string) {
        const byteCharacters = atob(base64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }
        const url = URL.createObjectURL(new Blob([byteArray], { type: 'application/pdf' }));
        const fileName = `Form433A_${(this.getTaxpayerName() || this.state.userId).replace(/\s+/g, '_')}.pdf`;

        Swal.fire({
            title: 'Form 433-A Preview',
            html: `<iframe src="${url}" title="Form 433-A Preview" style="width:100%;height:75vh;border:none;"></iframe>`,
            width: '90%',
            showCancelButton: true,
            confirmButtonText: 'Download',
            cancelButtonText: 'Close'
        }).then(result => {
            if (result.isConfirmed) {
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            URL.revokeObjectURL(url);
        });
    }

    private completeReview() {
        Swal.fire({
            title: 'Complete Form 433-A Review?',
            text: 'All changes will be saved and the review will be marked as completed.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Complete Review'
        }).then(result => {
            if (result.isConfirmed) {
                this.save(true).then(isSaved => {
                    if (isSaved) {
                        this.setState({ isReviewed: true });
                    }
                });
            }
        });
    }

    private openEvidenceList() {
        Swal.fire({
            title: 'Evidence List',
            html: `<div id="evidence-list-root"></div>`,
            width: '60%',
            showCloseButton: true,
            showConfirmButton: false,
            allowOutsideClick: false,
            didOpen: () => {
                const container = document.getElementById('evidence-list-root');
                if (container) {
                    const root = createRoot(container);
                    root.render(<EvidenceList userId={this.state.userId} />);
                }
            },
            willClose: () => {
                const container = document.getElementById('evidence-list-root');
                if (container) {
                    container.innerHTML = ''; // cleanup
                }
            }
        });
    }

    private scrollToFirstError(errorKey: string) {
        const questionId = errorKey.split('|')[0];
        this.scrollTo(`f433-q-${questionId}`);
    }

    /** Scrolls the page container so the element sits just below the sticky header. */
    private scrollTo(elementId: string) {
        const element = document.getElementById(elementId);
        const container = element?.closest('.body-container') as HTMLElement | null;
        if (!element) {
            return;
        }
        if (!container) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        const stickyBottom = this.stickyRef.current ? this.stickyRef.current.getBoundingClientRect().bottom : container.getBoundingClientRect().top;
        const top = container.scrollTop + element.getBoundingClientRect().top - stickyBottom - 12;
        container.scrollTo({ top, behavior: 'smooth' });
    }

    // #endregion
}

export default Form433AReview;
